interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface DeviceInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  isInstalled: boolean;
  isOnline: boolean;
}

// Unified storage keys
const STORAGE_KEYS = {
  PROMPT_SHOWN: 'scanvcf-install-prompt-shown',
  PROMPT_EXPIRE: 'scanvcf-install-prompt-expire',
  BANNER_CLOSED: 'scanvcf-banner-closed',
  INSTALLED: 'scanvcf-pwa-installed'
} as const;

class PWAService {
  private installPrompt: BeforeInstallPromptEvent | null = null;
  private isInstallPromptShown = false;
  private promptListeners: Array<(prompt: BeforeInstallPromptEvent | null) => void> = [];

  constructor() {
    console.log('[PWAService] constructor');
    this.init();
  }

  private init() {
    console.log('[PWAService] init start');
    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWAService] beforeinstallprompt fired');
      e.preventDefault();
      this.installPrompt = e as BeforeInstallPromptEvent;
      this.notifyPromptListeners(this.installPrompt);
    });

    // Listen for appinstalled event
    window.addEventListener('appinstalled', () => {
      console.log('[PWAService] appinstalled fired');
      this.installPrompt = null;
      this.isInstallPromptShown = true;
      // Mark as installed in localStorage
      localStorage.setItem(STORAGE_KEYS.INSTALLED, 'true');
      localStorage.setItem('pwa-installed', 'true');
      this.notifyPromptListeners(null);
    });
    console.log('[PWAService] init complete');
  }

  // Register callback for install prompt changes
  onInstallPromptChange(callback: (prompt: BeforeInstallPromptEvent | null) => void) {
    this.promptListeners.push(callback);
    // Immediately call with current state
    callback(this.installPrompt);
    
    return () => {
      const index = this.promptListeners.indexOf(callback);
      if (index > -1) {
        this.promptListeners.splice(index, 1);
      }
    };
  }

  private notifyPromptListeners(prompt: BeforeInstallPromptEvent | null) {
    this.promptListeners.forEach(callback => callback(prompt));
  }

  private isPromptCooldownActive(): boolean {
    const shownTime = localStorage.getItem(STORAGE_KEYS.PROMPT_SHOWN);
    if (!shownTime) return false;

    const expireTime = localStorage.getItem(STORAGE_KEYS.PROMPT_EXPIRE);
    if (!expireTime) return false;

    return Date.now() < parseInt(expireTime, 10);
  }

  // Check if install prompt should be shown
  shouldShowInstallPrompt(): boolean {
    if (this.isInstallPromptShown) {
      return false;
    }

    // Check if already shown recently
    if (this.isPromptCooldownActive()) {
      return false;
    }

    return true;
  }

  private shouldShowIOSManualPrompt(deviceInfo: DeviceInfo): boolean {
    // iOS Safari does not reliably provide beforeinstallprompt.
    if (!deviceInfo.isIOS || deviceInfo.isInstalled) {
      return false;
    }

    if (this.isInstallPromptShown || this.isPromptCooldownActive()) {
      return false;
    }

    return true;
  }

  private shouldShowAndroidManualPrompt(deviceInfo: DeviceInfo): boolean {
    // Android Chrome can sometimes skip beforeinstallprompt after uninstall/history state.
    // In that case, show manual install guidance modal.
    if (!deviceInfo.isAndroid || deviceInfo.isInstalled) {
      return false;
    }

    // If install prompt is available, prefer native prompt path.
    if (this.installPrompt) {
      return false;
    }

    if (this.isInstallPromptShown || this.isPromptCooldownActive()) {
      return false;
    }

    return true;
  }

  // Check if banner should be shown
  shouldShowBanner(): boolean {
    if (!this.installPrompt) {
      console.log('[PWAService] No install prompt available');
      return false;
    }

    // Check if banner was closed recently
    const closedTime = localStorage.getItem(STORAGE_KEYS.BANNER_CLOSED);
    if (closedTime) {
      const expireTime = parseInt(closedTime, 10);
      if (Date.now() < expireTime) {
        console.log('[PWAService] Banner was closed recently');
        return false;
      }
    }

    console.log('[PWAService] Banner should be shown');
    return true;
  }

  // Unified method to check if any PWA prompt should be shown
  shouldShowAnyPrompt(): { showModal: boolean; showBanner: boolean } {
    const deviceInfo = this.getDeviceInfo();
    
    console.log('[PWAService] shouldShowAnyPrompt - Device info:', deviceInfo);
    console.log('[PWAService] shouldShowAnyPrompt - Install prompt available:', !!this.installPrompt);
    
    // If already installed, don't show any prompt
    if (deviceInfo.isInstalled) {
      console.log('[PWAService] Already installed, not showing prompts');
      return { showModal: false, showBanner: false };
    }

    // Mobile manual fallback when native prompt is unavailable.
    if (deviceInfo.isIOS || deviceInfo.isAndroid) {
      const showIOSManual = this.shouldShowIOSManualPrompt(deviceInfo);
      const showAndroidManual = this.shouldShowAndroidManualPrompt(deviceInfo);
      const showModal = showIOSManual || showAndroidManual;
      console.log('[PWAService] mobile manual prompt:', {
        showIOSManual,
        showAndroidManual,
        showModal
      });
      if (showModal) {
        return { showModal: true, showBanner: false };
      }
    }

    // Desktop/Android native beforeinstallprompt path.
    if (!this.installPrompt) {
      console.log('[PWAService] No install prompt available, not showing prompts');
      return { showModal: false, showBanner: false };
    }

    // Check if modal prompt should be shown (higher priority)
    const showModal = !!this.installPrompt && this.shouldShowInstallPrompt();
    console.log('[PWAService] Should show modal:', showModal);
    
    // Check if banner should be shown (only if modal is not shown)
    const showBanner = !showModal && this.shouldShowBanner();
    console.log('[PWAService] Should show banner:', showBanner);

    return { showModal, showBanner };
  }

  // Install PWA
  async install(): Promise<boolean> {
    if (!this.installPrompt) {
      return false;
    }

    try {
      await this.installPrompt.prompt();
      const { outcome } = await this.installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('ScanVCF PWA installation accepted');
        this.markPromptShown();
        return true;
      } else {
        console.log('ScanVCF PWA installation dismissed');
        return false;
      }
    } catch (error) {
      console.error('ScanVCF PWA installation failed:', error);
      return false;
    }
  }

  // Mark prompt as shown
  markPromptShown() {
    this.isInstallPromptShown = true;
    const expireTime = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
    localStorage.setItem(STORAGE_KEYS.PROMPT_SHOWN, Date.now().toString());
    localStorage.setItem(STORAGE_KEYS.PROMPT_EXPIRE, expireTime.toString());
  }

  // Mark banner as closed
  markBannerClosed() {
    const expireTime = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
    localStorage.setItem(STORAGE_KEYS.BANNER_CLOSED, expireTime.toString());
  }

  // Register service worker
  async registerServiceWorker(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.log('ScanVCF: Service Worker not supported');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('ScanVCF: Service Worker registered successfully', registration);
      return true;
    } catch (error) {
      console.error('ScanVCF: Service Worker registration failed:', error);
      return false;
    }
  }

  // Check for service worker updates
  async checkForUpdates(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
        return true;
      }
      return false;
    } catch (error) {
      console.error('ScanVCF: Service Worker update check failed:', error);
      return false;
    }
  }

  // Request notification permission
  async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.log('ScanVCF: Notifications not supported');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error('ScanVCF: Notification permission request failed:', error);
      return 'denied';
    }
  }

  // Send notification
  sendNotification(title: string, options?: NotificationOptions) {
    if (Notification.permission === 'granted') {
      new Notification(title, {
        icon: '/icon-192x192.png',
        badge: '/icon-192x192.png',
        ...options,
      });
    }
  }

  // Get device info
  getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        (window.navigator as any).standalone === true;
    
    // Use runtime display mode to detect actual installation state.
    const isInstalledByDisplayMode = isStandalone ||
      window.matchMedia('(display-mode: fullscreen)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://');

    // If app was removed, clear stale install flags so reinstall prompts can appear again.
    const hasStaleFlag =
      localStorage.getItem(STORAGE_KEYS.INSTALLED) === 'true' ||
      localStorage.getItem('pwa-installed') === 'true';
    if (!isInstalledByDisplayMode && hasStaleFlag) {
      localStorage.removeItem(STORAGE_KEYS.INSTALLED);
      localStorage.removeItem('pwa-installed');
      // Reset prompt cooldown after uninstall so reinstall guidance can reappear immediately.
      localStorage.removeItem(STORAGE_KEYS.PROMPT_SHOWN);
      localStorage.removeItem(STORAGE_KEYS.PROMPT_EXPIRE);
      localStorage.removeItem(STORAGE_KEYS.BANNER_CLOSED);
      this.isInstallPromptShown = false;
    }

    return {
      isIOS,
      isAndroid,
      isStandalone,
      isInstalled: isInstalledByDisplayMode,
      isOnline: navigator.onLine
    };
  }

  // Get comprehensive PWA status
  getPWAStatus() {
    const deviceInfo = this.getDeviceInfo();
    
    return {
      isInstalled: deviceInfo.isInstalled,
      canInstall: !!this.installPrompt && !deviceInfo.isInstalled,
      hasServiceWorker: 'serviceWorker' in navigator,
      hasNotifications: 'Notification' in window,
      isOnline: deviceInfo.isOnline,
      deviceType: {
        isMobile: deviceInfo.isIOS || deviceInfo.isAndroid,
        isIOS: deviceInfo.isIOS,
        isAndroid: deviceInfo.isAndroid,
        isDesktop: !deviceInfo.isIOS && !deviceInfo.isAndroid
      }
    };
  }
}

// Export singleton instance
export const pwaService = new PWAService();